import { withLenses } from '@dhmk/zustand-lens';
import { useMemo } from 'react';
import { useStoreStateLensFocus } from 'src/providers/StoreStateLensFocusProvider';
import { useStoreState, useStoreSubscription } from 'src/providers/StoreStateProvider';
import { immer } from 'zustand/middleware/immer';

export type UseStoreState<T> = {
  <U extends keyof T>(selector: (state: T) => T[U]): T[U];
  <ReturnType>(selector: (state: T) => ReturnType): ReturnType;
};

export function createUseStoreState<State>(lens: string) {
  return <Selected>(selector: (state: State) => Selected): Selected => {
    const lensSelector = useStoreStateLensFocus(lens);

    return useStoreState((state: Record<string, State>) => {
      return selector(lensSelector(state)) as Selected;
    });
  };
}

export function createUseStoreSubscription<State>(lens: string) {
  return <Selected>(
    selector: (state: State) => Selected,
    effect: (state: Selected, prevState: Selected) => void,
    options?: { equalityFn?: (state: Selected, newState: Selected) => boolean; fireImmediately?: boolean }
  ): void => {
    const lensSelector = useStoreStateLensFocus(lens);

    return useStoreSubscription(
      (state: Record<string, State>) => {
        return selector(lensSelector(state)) as Selected;
      },
      effect,
      options
    );
  };
}

export function useLensedStore(lenses: Parameters<typeof withLenses>[0], deps?: unknown[]) {
  return useMemo(() => immer(withLenses(lenses)), [lenses, ...(deps ?? [])]);
}
